class NavigationDrawer {
  constructor() {
    this.drawer = document.getElementById("nav-drawer");
    this.openBtn = document.querySelector(".header__burger");
    this.closeBtns = document.querySelectorAll("[data-close]");

    if (!this.drawer || !this.openBtn) return;

    this.bind();
    this.initAccordions();
  }

  bind() {
    this.openBtn.addEventListener("click", () => this.open());

    this.closeBtns.forEach((btn) => {
      btn.addEventListener("click", () => this.close());
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });
  }

  open() {
    this.drawer.hidden = false;

    requestAnimationFrame(() => {
      this.drawer.classList.add("is-open");
    });

    this.openBtn.setAttribute("aria-expanded", "true");
    document.body.classList.add("nav-lock");
  }

  close() {
    this.drawer.classList.remove("is-open");
    this.openBtn.setAttribute("aria-expanded", "false");
    document.body.classList.remove("nav-lock");

    setTimeout(() => {
      this.drawer.hidden = true;
    }, 500);
  }

  initAccordions() {
    const items = document.querySelectorAll("[data-accordion]");

    items.forEach((item) => {
      const trigger = item.querySelector(".nav-item__trigger");

      trigger.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-open");

        // Close all top-level accordions + their nested sub-accordions
        items.forEach((i) => {
          i.classList.remove("is-open");
          i.querySelector(".nav-item__trigger")?.setAttribute(
            "aria-expanded",
            "false",
          );
          i.querySelectorAll("[data-sub-accordion]").forEach((sub) => {
            sub.classList.remove("is-open");
            sub
              .querySelector(".nav-item__child--heading")
              ?.setAttribute("aria-expanded", "false");
          });
        });

        if (!isOpen) {
          item.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });

    // Sub-accordions (3rd-level groups)
    const subItems = document.querySelectorAll("[data-sub-accordion]");

    subItems.forEach((sub) => {
      const trigger = sub.querySelector(".nav-item__child--heading");

      trigger.addEventListener("click", () => {
        const isOpen = sub.classList.contains("is-open");

        // Close sibling sub-accordions within the same parent
        const parent = sub.closest(".nav-item__children");
        parent?.querySelectorAll("[data-sub-accordion]").forEach((s) => {
          s.classList.remove("is-open");
          s.querySelector(".nav-item__child--heading")?.setAttribute(
            "aria-expanded",
            "false",
          );
        });

        if (!isOpen) {
          sub.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });
  }
}

// Toggles `past-hero` on <body> when the homepage hero banner has scrolled
// out of view. Used to reveal the floating pill nav after the hero.
class PastHero {
  constructor() {
    const banner = document.querySelector(".section__hero-banner");
    if (!banner) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        document.body.classList.toggle("past-hero", !entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(banner);
  }
}

// Scrollspy for the floating page-anchor nav. Marks the link whose target
// section is currently most prominent in the viewport (closest to a trigger
// line near the top) with `is-active`.
class PageAnchorScrollSpy {
  constructor() {
    const nav = document.querySelector(".page-anchors");
    if (!nav) return;

    this.pairs = [];
    nav.querySelectorAll(".page-anchors__link").forEach((link) => {
      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#") || href.length < 2) return;
      const target = document.querySelector(href);
      if (target) this.pairs.push({ link, target });
    });
    if (!this.pairs.length) return;

    this.bindClicks();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        this.update();
        ticking = false;
      });
    };

    this.update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
  }

  bindClicks() {
    this.pairs.forEach(({ link, target }) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        // Land the section's *content* (past its own top padding) at ~10%
        // of viewport from the top — gives breathing room without leaving
        // a big gap from the section's padding.
        const paddingTop =
          parseFloat(getComputedStyle(target).paddingTop) || 0;
        const offset = window.innerHeight * 0.1;
        const y =
          target.getBoundingClientRect().top +
          window.scrollY +
          paddingTop -
          offset;
        window.scrollTo({ top: y, behavior: "smooth" });
        history.replaceState(null, "", link.getAttribute("href"));
      });
    });
  }

  update() {
    // Active = last section whose top has crossed above the trigger line
    // (1/3 down from the top of the viewport).
    const trigger = window.innerHeight / 3;
    let activeIdx = -1;
    this.pairs.forEach(({ target }, idx) => {
      if (target.getBoundingClientRect().top <= trigger) activeIdx = idx;
    });

    // When the user has reached the bottom of the page, force the last link
    // active — short trailing sections may never reach the trigger line.
    const atBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - 2;
    if (atBottom) activeIdx = this.pairs.length - 1;

    this.pairs.forEach(({ link }, idx) => {
      link.classList.toggle("is-active", idx === activeIdx);
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new NavigationDrawer();
  new PastHero();
  new PageAnchorScrollSpy();
});
